import { z } from 'zod';

import { EmailSchema, IsoDateTimeSchema, UuidSchema } from './common.js';

/**
 * Website customer authentication (public site login).
 *
 * Distinct from `auth.ts`, which covers *staff* login to the CRM. Flow:
 *
 *   1. Single identity field (email OR mobile) → check
 *   2a. New → name + email + phone → email OTP → register (session)
 *   2b. Existing / guest claim → email OTP → login (session)
 *
 * Social (Google / Facebook) remains a shortcut past the email OTP steps.
 * Website login is passwordless; `customers.password_hash` is optional.
 *
 * The customer never sees a token: the API issues an HttpOnly cookie pair, so
 * these response schemas deliberately carry no `accessToken` field.
 */

/** How a customer most recently proved who they are. */
export const CustomerAuthProviderSchema = z.enum(['EMAIL_OTP', 'GOOGLE', 'FACEBOOK']);
export type CustomerAuthProvider = z.infer<typeof CustomerAuthProviderSchema>;

/**
 * Phone as typed into the login / register form. Digits, spaces, dashes,
 * brackets and a leading `+`.
 */
export const CustomerPhoneSchema = z
  .string()
  .trim()
  .min(6, 'Enter a valid phone number')
  .max(20, 'Enter a valid phone number')
  .regex(/^\+?[\d\s()-]+$/, 'Enter a valid phone number')
  .describe('Contact phone number');

/** Dial code captured alongside the number, e.g. `+91`. */
export const CustomerDialCodeSchema = z
  .string()
  .trim()
  .regex(/^\+\d{1,4}$/, 'Invalid country dial code')
  .describe('E.164 country dial code, e.g. +91');

export const CustomerFullNameSchema = z
  .string()
  .trim()
  .min(2, 'Enter your full name')
  .max(120, 'Name is too long')
  .describe('Full name');

/** Optional leftover. Website login is OTP-only; hashes may still exist on older rows. */
export const CustomerPasswordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password is too long')
  .describe('Account password');

/** Six-digit one-time code delivered by email. */
export const CustomerOtpSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, 'Enter the 6-digit code')
  .describe('One-time code emailed to the customer. Single use, short TTL.');

/**
 * Single identity field: email address OR mobile digits.
 * The server decides which by looking for `@`.
 */
export const CustomerEmailOrMobileSchema = z
  .string()
  .trim()
  .min(3, 'Enter your email or mobile number')
  .max(254, 'Enter a valid email or mobile number')
  .describe('Email address or mobile number');

// ----------------------------------------------------------------------------
// Step 1 - "does this person already have an account?"
// ----------------------------------------------------------------------------

export const CustomerAuthCheckRequestSchema = z.object({
  emailOrMobile: CustomerEmailOrMobileSchema,
  dialCode: CustomerDialCodeSchema.optional().describe('Defaults to +91 when omitted'),
});
export type CustomerAuthCheckRequest = z.infer<typeof CustomerAuthCheckRequestSchema>;

export const CustomerAuthCheckResponseSchema = z.object({
  exists: z
    .boolean()
    .describe('True when a verified or claimable guest customer matches this identity'),
  needsRegistration: z
    .boolean()
    .describe('True when the client must show the name + email + phone registration form'),
  hasPassword: z
    .boolean()
    .describe('Legacy: true when the matched account has a password hash (unused by the UI)'),
  maskedEmail: z
    .string()
    .describe('Obfuscated address to echo in the "code sent to..." caption (empty if unknown)'),
  /** Resolved inbox for OTP when identity was a phone match; else the typed email or "". */
  email: z.string().describe('Email the OTP will be sent to once known'),
});
export type CustomerAuthCheckResponse = z.infer<typeof CustomerAuthCheckResponseSchema>;

// ----------------------------------------------------------------------------
// Step 2 - request a code
// ----------------------------------------------------------------------------

export const CustomerOtpSendRequestSchema = z.object({
  email: EmailSchema,
  phone: CustomerPhoneSchema.optional(),
  dialCode: CustomerDialCodeSchema.optional(),
  fullName: CustomerFullNameSchema.optional(),
});
export type CustomerOtpSendRequest = z.infer<typeof CustomerOtpSendRequestSchema>;

export const CustomerOtpSendResponseSchema = z.object({
  message: z.string().describe('Generic copy - identical whether or not the address is known'),
  resendAfterSeconds: z
    .number()
    .int()
    .nonnegative()
    .describe('Seconds the client must wait before the resend button re-enables'),
  expiresInSeconds: z.number().int().positive().describe('Lifetime of the code just issued'),
});
export type CustomerOtpSendResponse = z.infer<typeof CustomerOtpSendResponseSchema>;

// ----------------------------------------------------------------------------
// Register (new account) - OTP in one shot. Password is optional leftover.
// ----------------------------------------------------------------------------

export const CustomerRegisterRequestSchema = z.object({
  fullName: CustomerFullNameSchema,
  email: EmailSchema,
  phone: CustomerPhoneSchema,
  dialCode: CustomerDialCodeSchema.optional(),
  password: CustomerPasswordSchema.optional(),
  otp: CustomerOtpSchema,
});
export type CustomerRegisterRequest = z.infer<typeof CustomerRegisterRequestSchema>;

// ----------------------------------------------------------------------------
// Existing account - redeem OTP and start a session
// ----------------------------------------------------------------------------

export const CustomerOtpVerifyRequestSchema = z.object({
  email: EmailSchema,
  otp: CustomerOtpSchema,
});
export type CustomerOtpVerifyRequest = z.infer<typeof CustomerOtpVerifyRequestSchema>;

/** @deprecated Login now returns CustomerLoginResponse from POST /otp/verify. */
export const CustomerOtpVerifyResponseSchema = z.object({
  proofToken: z.string().describe('Legacy short-lived token; unused by passwordless login'),
  hasPassword: z.boolean(),
  maskedEmail: z.string(),
});
export type CustomerOtpVerifyResponse = z.infer<typeof CustomerOtpVerifyResponseSchema>;

// ----------------------------------------------------------------------------
// Password login - email/mobile + password. Falls back to OTP-verified
// set/reset when there is no password yet, it doesn't match, or the account
// doesn't exist.
// ----------------------------------------------------------------------------

export const CustomerPasswordLoginRequestSchema = z.object({
  emailOrMobile: CustomerEmailOrMobileSchema,
  dialCode: CustomerDialCodeSchema.optional(),
  password: CustomerPasswordSchema,
});
export type CustomerPasswordLoginRequest = z.infer<typeof CustomerPasswordLoginRequestSchema>;

export const CustomerPasswordLoginStatusSchema = z.enum([
  'OK',
  'NOT_FOUND',
  'PASSWORD_NOT_SET',
  'MISMATCH',
]);
export type CustomerPasswordLoginStatus = z.infer<typeof CustomerPasswordLoginStatusSchema>;

// CustomerPasswordLoginResponseSchema is defined further down, after
// AuthenticatedCustomerSchema (Session identity section) which it references.

// ----------------------------------------------------------------------------
// Reset / first-time password step - OTP proves ownership, then a proof token
// unlocks setting a new password. Used for guest accounts with no password
// yet, a forgotten password, and "forgot password".
// ----------------------------------------------------------------------------

export const CustomerResetVerifyResponseSchema = z.object({
  proofToken: z.string().describe('Short-lived token that unlocks POST /password'),
  maskedEmail: z.string(),
  needsProfile: z
    .boolean()
    .describe('True when the account is missing a name or phone the client should collect'),
});
export type CustomerResetVerifyResponse = z.infer<typeof CustomerResetVerifyResponseSchema>;

export const CustomerPasswordCompleteRequestSchema = z.object({
  proofToken: z.string().min(1),
  password: CustomerPasswordSchema,
  fullName: CustomerFullNameSchema.optional(),
  phone: CustomerPhoneSchema.optional(),
  dialCode: CustomerDialCodeSchema.optional(),
});
export type CustomerPasswordCompleteRequest = z.infer<typeof CustomerPasswordCompleteRequestSchema>;

// ----------------------------------------------------------------------------
// Change password - already signed in, no OTP needed since the session cookie
// already proves ownership.
// ----------------------------------------------------------------------------

export const CustomerChangePasswordRequestSchema = z.object({
  password: CustomerPasswordSchema,
});
export type CustomerChangePasswordRequest = z.infer<typeof CustomerChangePasswordRequestSchema>;

// ----------------------------------------------------------------------------
// Social sign-in
// ----------------------------------------------------------------------------

export const CustomerGoogleLoginRequestSchema = z.object({
  credential: z
    .string()
    .min(1)
    .describe('Google ID token (JWT) from Google Identity Services - verified server-side'),
});
export type CustomerGoogleLoginRequest = z.infer<typeof CustomerGoogleLoginRequestSchema>;

export const CustomerFacebookLoginRequestSchema = z.object({
  accessToken: z
    .string()
    .min(1)
    .describe('Facebook user access token from the JS SDK - verified server-side via debug_token'),
});
export type CustomerFacebookLoginRequest = z.infer<typeof CustomerFacebookLoginRequestSchema>;

// ----------------------------------------------------------------------------
// Session identity
// ----------------------------------------------------------------------------

export const AuthenticatedCustomerSchema = z.object({
  id: UuidSchema,
  email: z.string(),
  fullName: z.string(),
  firstName: z.string().nullable(),
  phone: z.string().nullable().describe('Dial code + number as captured, or null'),
  emailVerified: z.boolean(),
  provider: CustomerAuthProviderSchema.nullable(),
  lastLoginAt: IsoDateTimeSchema.nullable(),
});
export type AuthenticatedCustomer = z.infer<typeof AuthenticatedCustomerSchema>;

/**
 * Returned by every successful login. No tokens: the access and refresh JWTs
 * ride on `Set-Cookie` as HttpOnly, so nothing script-readable is handed back.
 */
export const CustomerLoginResponseSchema = z.object({
  customer: AuthenticatedCustomerSchema,
  isNewAccount: z.boolean().describe('True when this login created the customer row'),
});
export type CustomerLoginResponse = z.infer<typeof CustomerLoginResponseSchema>;

export const CustomerPasswordLoginResponseSchema = z.object({
  status: CustomerPasswordLoginStatusSchema,
  customer: AuthenticatedCustomerSchema.optional(),
  isNewAccount: z.boolean().optional(),
  /** Set on PASSWORD_NOT_SET / MISMATCH so the client knows where to send the reset OTP. */
  email: z.string().optional(),
});
export type CustomerPasswordLoginResponse = z.infer<typeof CustomerPasswordLoginResponseSchema>;
